import React, {useEffect, useRef, useState} from 'react';
import useCompanyId from "../Hooks/useCompanyId";
import {
    deleteCustomTimelineEventCategory,
    getTimelineEventCategoriesForCompany,
    registerCustomTimelineEventCategory,
    updateCustomTimelineEventCategory,
    addTimelineLocalization,
    deleteTimelineLocalization,
} from "../API/companyApi";
import {toast} from "react-toastify";
import messageFromError from "../utils/errorUtils";
import ConfirmModal from "../components/Modals/ConfirmModal";
import MyTable from "../components/core/Components/MyTable";
import {Button, Form, FormCheck, FormControl, FormLabel, OverlayTrigger, Spinner, Tooltip} from "react-bootstrap";
import useMyTranslation from "../Hooks/useMyTranslation";
import InfoIcon from "../components/InfoIcon";
import {faEye, faEyeSlash, faPlus, faTrashCan, faEarthAmericas} from "@fortawesome/free-solid-svg-icons";
import IconButton from "../components/IconButton";
import ActionModal from "../components/Modals/ActionModals/ActionModal";
import ConditionalRender, {Fallback} from "../components/ConditionalRender";
import {faCopy, faPenToSquare} from "@fortawesome/free-regular-svg-icons";
import ClaimGuard from "../components/Auth/ClaimGuard";
import {MANAGE_TIMELINE, MANAGE_TIMELINE_ALL} from "../Claims";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

const EXTERNAL_ID_MAX_LENGTH = 50;

const isExternalIdTooLong = (externalId) => (externalId?.trim().length ?? 0) > EXTERNAL_ID_MAX_LENGTH;

const TimelineEventsPage = () => {

    const companyId = useCompanyId()
    const t = useMyTranslation('pages.timelineEvents')

    const [eventCategories, setEventCategories] = useState([])
    const [categoryToBeCreated, setCategoryToBeCreated] = useState({
        name: '',
        hasDuration: false,
        workOrderAssignmentRequired: false,
        subtype: null,
        priority: null,
        externalId: '',
    })

    const [showCreateCustomCategoryModal, setShowCreateCustomCategoryModal] = useState(false)

    const [categoryForUpdate, setCategoryForUpdate] = useState()
    const [categoryForDeletion, setCategoryForDeletion] = useState()
    // const [categoryLocalization, setcategoryLocalization] = useState()
    const [copied, setCopied] = useState(null);
    const copyTimeoutRef = useRef(null);
    const [createdCategory, setCreatedCategory] = useState(null);
    const [showCreatedModal, setShowCreatedModal] = useState(false);
    const [categoryLocalization, setCategoryLocalization] = useState(null);
    const [localizations, setLocalizations] = useState([]); // display only
    const [localizationToDelete, setLocalizationToDelete] = useState(null);
    // { categoryId, languageCode }

    const [newLocalization, setNewLocalization] = useState(null);
    // { languageCode: '', value: '' }
    const [updatingVisibilityOfCategoryIds, setUpdatingVisibilityOfCategoryIds] = useState([]);

    useEffect(() => {
        if (companyId) {
            getCategories()
        }
    }, [companyId]);

    async function getCategories() {
        const response = await getTimelineEventCategoriesForCompany(companyId);

        const sortedCategories = response.data.sort((a, b) => {
            if (a.type !== 'Custom' && b.type === 'Custom') return -1;
            if (a.type === 'Custom' && b.type !== 'Custom') return 1;

            return a.name.localeCompare(b.name);
        });

        setEventCategories(sortedCategories);
    }


    async function createCustomCategory() {
        await registerCustomTimelineEventCategory(companyId, categoryToBeCreated);

        setCategoryToBeCreated({
            name: '',
            hasDuration: false,
            workOrderAssignmentRequired: false,
            subtype: null,
            priority: null,
            externalId: '',
        });
        // refetch categories
        const response = await getTimelineEventCategoriesForCompany(companyId);
        setEventCategories(response.data);

        const newCategory = response.data.find(
            c => c.name === categoryToBeCreated.name && c.type === 'Custom'
        );

        if (newCategory) {
            setCreatedCategory(newCategory);
            setShowCreatedModal(true);
        }
    }


    async function deleteCategory() {
        if (!categoryForDeletion)
            return;

        await deleteCustomTimelineEventCategory(categoryForDeletion.id);
        toast.success("Deleted");
        await getCategories();
    }

    async function updateCustomCategory() {
        if (!categoryForUpdate)
            return;

        await updateCustomTimelineEventCategory(categoryForUpdate.id, categoryForUpdate);

        await getCategories();
    }

    async function toggleVisibilityOfCategory(category) {
        const toggledCategory = {...category, visible: !category.visible};
        setUpdatingVisibilityOfCategoryIds(prev => [...prev, category.id]);

        try {
            await updateCustomTimelineEventCategory(category.id, toggledCategory);
            await getCategories();
        } finally {
            setUpdatingVisibilityOfCategoryIds(prev => prev.filter(id => id !== category.id));
        }
    }

    const handleCopyCategory = (category) => {
        const textToCopy = `
            ID: ${category.id}
            ${t('eventsTable.name')}: ${category.name}
            ${t('eventsTable.type')}: ${category.type}
            ${t('externalId', 'dialogs')}: ${category.externalId ?? ''}
            ${t('hasDuration')}: ${category.hasDuration ? t('states.yes', 'global') : t('states.no', 'global')}
        `.trim();

        navigator.clipboard.writeText(textToCopy);
        toast.success(t('copied'));
    };



    const CategoryType = ({ type }) => {
        switch (type) {
            case 'System':
            case 'Private':
            case 'WorkLocation':
            case 'Travel':
            case 'Break':
                return <span>{t('eventTypes.system')}</span>;
            case 'Custom':
                return <span>{t('eventTypes.custom')}</span>;
            default:
                return type;
        }
    }
    function getFlag(languageCode) {
        switch (languageCode) {
            case 'nl': return '🇳🇱';
            case 'en': return '🇬🇧';
            case 'de': return '🇩🇪';
            case 'fr': return '🇫🇷';
            default: return '🌍';
        }
    }

    const SUPPORTED_LANGUAGES = [
        { code: 'nl', label: '🇳🇱' },
        { code: 'en', label: '🇬🇧' },
        { code: 'de', label: '🇩🇪' },
        { code: 'fr', label: '🇫🇷' },
    ];

    const LocalizationCreateRow = ({
        localization,
        existingLocalizations,
        onChange,
        onSubmit,
    }) => {
        const isValid =
            localization.languageCode &&
            localization.value.trim().length > 0;

        // languages already used
        const usedLanguageCodes = existingLocalizations.map(
            (l) => l.languageCode
        );

        // only show languages that are not yet used
        const availableLanguages = SUPPORTED_LANGUAGES.filter(
            (lang) => !usedLanguageCodes.includes(lang.code)
        );

        return (
            <div className="d-flex align-items-center gap-2 w-100">
                <Form.Select
                    size="sm"
                    value={localization.languageCode}
                    style={{ width: '10ch' }}
                    onChange={(e) =>
                        onChange({ languageCode: e.target.value })
                    }
                >
                    <option value="">🌍</option>

                    {availableLanguages.map(lang => (
                        <option key={lang.code} value={lang.code}>
                            {lang.label}
                        </option>
                    ))}
                </Form.Select>

                <FormControl
                    size="sm"
                    autoFocus
                    value={localization.value}
                    onChange={(e) =>
                        onChange({ value: e.target.value })
                    }
                />

                <Button
                    size="sm"
                    variant="success"
                    disabled={!isValid}
                    onClick={onSubmit}
                >
                    {t("add", "global.operations")}
                </Button>
            </div>
        );
    };


    return (
        <div>
            <div className={'d-flex justify-content-between align-items-center'}>
                <h1>{t('eventsTable.title')}</h1>
                <div>
                    <IconButton icon={faPlus} color="white" bg="#198754" onClick={() => setShowCreateCustomCategoryModal(true)} />
                </div>
            </div>
            <MyTable style={{ tableLayout: 'auto' }}>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>{t('eventsTable.name')}</th>
                        <th>{t('eventsTable.type')}</th>
                        <th>{t('externalId', 'dialogs')}</th>
                        <th>{t('hasDuration')} <InfoIcon text={t('tooltips.hasDuration')} /></th>
                        <ClaimGuard requiredClaim={MANAGE_TIMELINE}>
                            <th>{t('eventsTable.actions')}</th>
                        </ClaimGuard>
                    </tr>
                </thead>
                <tbody>
                    {eventCategories.map((category) => (
                        <tr key={category.id}>
                            <td>
                                <ConditionalRender condition={category.type === 'Custom'}>
                                    <span style={{
                                        backgroundColor: 'whitesmoke',
                                        padding: '2px',
                                        borderRadius: '5px',
                                        border: '1px solid gainsboro',
                                        cursor: 'pointer'
                                    }}
                                    onClick={async () => {
                                        await navigator.clipboard.writeText(category.id);
                                        toast.success(t('copied'));
                                    }}>
                                        {category.id}
                                    </span>
                                </ConditionalRender>
                            </td>

                            <td>{category.name}</td>
                            <td><CategoryType type={category.type} /></td>
                            <td>{category.externalId}</td>
                            <td>{category.hasDuration ? t('states.yes', 'global') : t('states.no', 'global')}</td>


                            <ClaimGuard requiredClaim={MANAGE_TIMELINE}>
                                <td>
                                    <ConditionalRender condition={category.type === 'Custom'}>
                                        <IconButton icon={faEarthAmericas}
                                            title={t('localizations.title')}
                                            onClick={() => {
                                                setCategoryLocalization(category);
                                                setLocalizations(category.localizations ?? []);
                                            }}
                                        />
                                        <IconButton
                                            icon={faCopy}
                                            title={t('copy')}
                                            onClick={() => handleCopyCategory(category)}
                                        />

                                        <IconButton icon={faPenToSquare}
                                            onClick={() => setCategoryForUpdate(category)} />

                                        <ClaimGuard requiredClaim={MANAGE_TIMELINE_ALL}>
                                            <ConditionalRender condition={!updatingVisibilityOfCategoryIds.includes(category.id)}>
                                                <IconButton icon={category.visible ? faEye : faEyeSlash}
                                                            onClick={() => toggleVisibilityOfCategory(category)} />
                                                <Fallback>
                                                    <Spinner size={"sm"} className={'mx-2'}/>
                                                </Fallback>
                                            </ConditionalRender>
                                        </ClaimGuard>

                                        <IconButton icon={faTrashCan} color="red"
                                            onClick={() => setCategoryForDeletion(category)} />

                                        <Fallback>
                                            <ClaimGuard requiredClaim={MANAGE_TIMELINE_ALL}>
                                                <ConditionalRender condition={!updatingVisibilityOfCategoryIds.includes(category.id)}>
                                                    <IconButton icon={category.visible ? faEye : faEyeSlash}
                                                                onClick={() => toggleVisibilityOfCategory(category)} />
                                                    <Fallback>
                                                        <div className={'px-2'}>
                                                            <Spinner size={"sm"}/>
                                                        </div>
                                                    </Fallback>
                                                </ConditionalRender>
                                            </ClaimGuard>
                                        </Fallback>
                                    </ConditionalRender>
                                </td>
                            </ClaimGuard>
                        </tr>
                    ))}
                </tbody>
            </MyTable>

            <ActionModal
                active={!!categoryLocalization}
                title={t("localizations.title")}
                hideOnConfirm
                onCancel={() => {
                    setCategoryLocalization(null);
                    setNewLocalization(null);
                }}
                hideNegativeButton = {true}
            >
                <div className="d-flex flex-column align-items-center gap-3">

                    {/* DISPLAY EXISTING LOCALIZATIONS */}
                    {localizations.map(loc => (
                        <div
                            key={loc.languageCode}
                            className="d-flex align-items-center gap-2 w-100"
                        >

                            <span style={{ width: 60 }}>
                                {getFlag(loc.languageCode)}
                            </span>

                            <FormControl
                                size="sm"
                                readOnly
                                value={loc.value}
                            />
                            <IconButton
                                icon={faTrashCan}
                                color="red"
                                onClick={() => {
                                    setLocalizationToDelete({
                                        categoryId: categoryLocalization.id,
                                        languageCode: loc.languageCode,
                                    });
                                }}
                            />


                        </div>
                    ))}

                    <div className="w-100 mb-2">
                        <div
                            className="d-flex align-items-start gap-2 p-2 rounded"
                            style={{
                                backgroundColor: '#f8f9fa',
                                fontSize: '0.875rem',
                            }}
                        >
                            <InfoIcon text={t('localizations.localizationsInfo')} />
                            <span>
                                {t('localizations.localizationsInfo')}
                            </span>
                        </div>
                    </div>

                    {/* NEW LOCALIZATION (only when + clicked) */}
                    {newLocalization && (
                        <LocalizationCreateRow
                            localization={newLocalization}
                            existingLocalizations={localizations}
                            onChange={(update) => {
                                setNewLocalization(prev => ({
                                    ...prev,
                                    ...update,
                                }));
                            }}
                            onSubmit={async () => {
                                try {
                                    await addTimelineLocalization(
                                        categoryLocalization.id,
                                        newLocalization
                                    );

                                    toast.success(
                                        t('localizations.addSuccess', 'global.notifications')
                                    );

                                    setLocalizations(prev => [...prev, newLocalization]);
                                    setNewLocalization(null);

                                    await getCategories();

                                } catch (error) {
                                    toast.error(messageFromError(error))
                                }
                            }}

                        />



                    )}

                    {/* + BUTTON */}
                    {!newLocalization && (
                        <div style={{ marginTop: '50px' }}>
                            <IconButton icon={faPlus} color="white" bg="#198754"
                                onClick={() =>
                                    setNewLocalization({ languageCode: '', value: '' })
                                }
                            >

                            </IconButton>
                        </div>
                    )}
                </div>
            </ActionModal>


            <ActionModal
                func={createCustomCategory}
                active={showCreateCustomCategoryModal}
                hideOnConfirm
                onCancel={() => setShowCreateCustomCategoryModal(false)}
                onConfirm={() => setShowCreateCustomCategoryModal(false)}
                title={t('createCategoryDialog.title')}
                positiveButton={{
                    variant: 'success',
                    disabled: categoryToBeCreated.name.trim().length < 1 || isExternalIdTooLong(categoryToBeCreated.externalId)
                }}>

                <FormLabel>{t('name', 'dialogs')}</FormLabel>
                <FormControl required type='text' value={categoryToBeCreated.name} onChange={(e) => {
                    setCategoryToBeCreated(prev => ({ ...prev, name: e.target.value }))
                }} />

                <FormLabel className={'mt-3'}>{t('externalId', 'dialogs')}</FormLabel>
                <FormControl type='text' value={categoryToBeCreated.externalId}
                    isInvalid={isExternalIdTooLong(categoryToBeCreated.externalId)}
                    onChange={(e) => {
                        setCategoryToBeCreated(prev => ({ ...prev, externalId: e.target.value }))
                    }} />
                <Form.Control.Feedback type='invalid'>{t('externalIdTooLong', 'dialogs')}</Form.Control.Feedback>

                <FormCheck
                    className={'mt-3'}
                    required
                    type='checkbox'
                    label={t('hasDuration')}
                    checked={categoryToBeCreated.hasDuration}
                    onChange={(e) => {
                        setCategoryToBeCreated(prev => ({ ...prev, hasDuration: e.target.checked }))
                    }} />

                <small className={'d-block small-text'}>{t('tooltips.hasDuration')}</small>
            </ActionModal>

            <ActionModal
                func={updateCustomCategory}
                active={categoryForUpdate}
                hideOnConfirm
                onCancel={() => setCategoryForUpdate(null)}
                onConfirm={() => {
                    setCategoryForUpdate(null);
                }}
                title={t('updateCategoryDialog.title')}
                positiveButton={{
                    variant: 'success',
                    disabled: categoryForUpdate?.name.trim().length < 1 || isExternalIdTooLong(categoryForUpdate?.externalId)
                }}>

                <FormLabel>{t('name', 'dialogs')}</FormLabel>
                <FormControl required type='text' value={categoryForUpdate?.name} onChange={(e) => {
                    setCategoryForUpdate(prev => ({ ...prev, name: e.target.value }))
                }} />

                <FormLabel className={'mt-3'}>{t('externalId', 'dialogs')}</FormLabel>
                <FormControl type='text' value={categoryForUpdate?.externalId ?? ''}
                    isInvalid={isExternalIdTooLong(categoryForUpdate?.externalId)}
                    onChange={(e) => {
                        setCategoryForUpdate(prev => ({ ...prev, externalId: e.target.value }))
                    }} />
                <Form.Control.Feedback type='invalid'>{t('externalIdTooLong', 'dialogs')}</Form.Control.Feedback>

                <FormCheck
                    className={'mt-3'}
                    required
                    type='checkbox'
                    label={t('hasDuration')}
                    checked={categoryForUpdate?.hasDuration}
                    onChange={(e) => {
                        setCategoryForUpdate(prev => ({ ...prev, hasDuration: e.target.checked }))
                    }} />

                <small className={'d-block small-text'}>{t('tooltips.hasDuration')}</small>
            </ActionModal>

            <ActionModal
                func={deleteCategory}
                active={categoryForDeletion}
                hideOnConfirm
                onCancel={() => setCategoryForDeletion(null)}
                onConfirm={() => setCategoryForDeletion(null)}
                title={t('deleteCategoryDialog.title')}
                positiveButton={{
                    variant: 'danger'
                }}>

                <span>{t('confirmDeleteDialog.youAreAboutTo', 'dialogs')} <strong style={{ color: 'red' }}>{t('confirmDeleteDialog.delete', 'dialogs')}</strong> <strong>{categoryForDeletion?.name}</strong>. {t('confirmDeleteDialog.areYouSure', 'dialogs')}</span>
            </ActionModal>

            <ConfirmModal
                active={!!localizationToDelete}
                title={t('localizations.confirmDeleteLocalization')}
                positiveButton={{ text: t('localizations.delete'), variant: 'danger' }}
                negativeButtonText={t('localizations.cancel')}
                onConfirm={async () => {
                    if (!localizationToDelete) return;

                    try {
                        await deleteTimelineLocalization(
                            localizationToDelete.categoryId,
                            localizationToDelete.languageCode
                        );

                        setLocalizations(prev =>
                            prev.filter(
                                l => l.languageCode !== localizationToDelete.languageCode
                            )
                        );

                        toast.success(t('localizations.deleted'));

                        await getCategories();
                    } catch(error) {
                        toast.error(messageFromError(error))
                    } finally {
                        setLocalizationToDelete(null);
                    }
                }}
                onCancel={() => setLocalizationToDelete(null)}
            >
                <span>
                    {t('localizations.areYouSureDeleteLocalization')}
                </span>
            </ConfirmModal>


            <ActionModal
                active={showCreatedModal}
                hideOnConfirm
                onConfirm={() => setShowCreatedModal(false)}
                title={createdCategory ? t('eventsTable.createdTitle').replace('{name}', createdCategory.name) : ''}
                positiveButton={{ variant: 'success', text: t('ok') }}
                hideNegativeButton={true}
            >
                {createdCategory && (
                    <div>
                        <p>{t('eventsTable.createdMessage')}</p>

                        <div className="position-relative mb-2">
                            <Form.Control
                                as="textarea"
                                readOnly
                                value={`ID: ${createdCategory.id}
                                        ${t('eventsTable.name')}: ${createdCategory.name}
                                        ${t('eventsTable.type')}: ${createdCategory.type}
                                        ${t('externalId', 'dialogs')}: ${createdCategory.externalId ?? ''}
                                        ${t('hasDuration')}: ${createdCategory.hasDuration ? t('states.yes', 'global') : t('states.no', 'global')
                                    }
                                    `.trim()
                                }
                                style={{
                                    height: '8.5rem',
                                    resize: 'none',
                                    paddingRight: '2.5rem' // space for icon
                                }}
                            />

                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{copied ? t('copiedAll') : t('copyAll')}</Tooltip>}
                            >
                                <Button
                                    variant="link"
                                    className="position-absolute top-0 end-0 mt-1 me-1 p-1 text-secondary"
                                    onClick={() => {
                                        const textToCopy =
                                            `ID: ${createdCategory.id}
                                            ${t('eventsTable.name')}: ${createdCategory.name}
                                            ${t('eventsTable.type')}: ${createdCategory.type}
                                            ${t('externalId', 'dialogs')}: ${createdCategory.externalId ?? ''}
                                            ${t('hasDuration')}: ${createdCategory.hasDuration ? t('states.yes', 'global') : t('states.no', 'global')}
                                            `.trim();

                                        navigator.clipboard.writeText(textToCopy);
                                        setCopied(true);
                                        toast.success(t('copiedAll'));

                                        setTimeout(() => setCopied(false), 1500);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </Button>
                            </OverlayTrigger>
                        </div>
                    </div>
                )}
            </ActionModal>
        </div>
    );
};

export default TimelineEventsPage;